import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Typography,
  Box,
  Chip,
} from '@mui/material'
import {
  Download,
  Close,
  InsertDriveFile,
  PictureAsPdf,
  Description,
  Image as ImageIcon,
  VideoFile,
  AudioFile,
} from '@mui/icons-material'
import { formatDistanceToNow } from 'date-fns'
import { useConfig } from '../contexts/ConfigContext'
import { useAuth } from '../hooks/useAuth'
import {
  buildAttachmentUrl,
  downloadAuthenticatedFile,
} from '../utils/downloadAuthenticatedFile'

const getFileIcon = (contentType) => {
  if (!contentType) return <InsertDriveFile />

  if (contentType.includes('pdf')) return <PictureAsPdf color="error" />
  if (contentType.includes('image')) return <ImageIcon color="primary" />
  if (contentType.includes('video')) return <VideoFile color="secondary" />
  if (contentType.includes('audio')) return <AudioFile color="info" />
  if (contentType.includes('word') || contentType.includes('document')) return <Description color="primary" />

  return <InsertDriveFile />
}

const formatFileSize = (bytes) => {
  if (!bytes) return 'Unknown'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

const isImageType = (contentType) => contentType && contentType.startsWith('image/')

const AttachmentsModal = ({ open, onClose, ticket }) => {
  const config = useConfig()
  const { getAccessToken } = useAuth()
  const [imagePreviews, setImagePreviews] = useState({})

  const attachments = ticket?.data?.external_attachments || []

  // Load image previews when modal opens
  useEffect(() => {
    if (!open || !ticket?.id) {
      setImagePreviews({})
      return
    }

    const imageAttachments = attachments.filter(att => isImageType(att.content_type) && att.id)
    if (imageAttachments.length === 0) return

    let cancelled = false

    const loadPreviews = async () => {
      const token = await getAccessToken()
      if (!token || cancelled) return

      const baseUrl = config?.api?.baseUrl || '/api/v1'

      for (const att of imageAttachments) {
        if (cancelled) break
        try {
          const url = `${baseUrl}/tickets/${ticket.id}/attachments/${att.id}/download`
          const response = await fetch(url, {
            headers: { 'Authorization': `Bearer ${token}` },
          })
          if (!response.ok) continue
          const blob = await response.blob()
          if (cancelled) break
          const blobUrl = URL.createObjectURL(blob)
          setImagePreviews(prev => ({ ...prev, [att.id]: blobUrl }))
        } catch {
          // Skip failed previews silently
        }
      }
    }

    loadPreviews()

    return () => {
      cancelled = true
      // Revoke all blob URLs on cleanup
      setImagePreviews(prev => {
        Object.values(prev).forEach(url => URL.revokeObjectURL(url))
        return {}
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when modal opens or ticket changes
  }, [open, ticket?.id])

  const handleDownload = async (attachment) => {
    try {
      await downloadAuthenticatedFile({
        url: buildAttachmentUrl(
          config?.api?.baseUrl,
          `/tickets/${ticket.id}/attachments/${attachment.id}/download`
        ),
        filename: attachment.filename,
        getAccessToken,
      })
    } catch (error) {
      console.error('Error downloading attachment:', error)
      alert('Failed to download attachment. Please try again.')
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Download />
          <Typography variant="h6">Ticket Attachments</Typography>
          <Chip
            label={attachments.length}
            size="small"
            color="primary"
            sx={{ ml: 1 }}
          />
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {attachments.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <InsertDriveFile sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              No attachments found
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>File</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Uploaded By</TableCell>
                  <TableCell>Uploaded</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attachments.map((attachment, index) => {
                  const uploadedDate = attachment.timestamp
                    ? new Date(attachment.timestamp)
                    : null
                  const previewUrl = imagePreviews[attachment.id]

                  return (
                    <React.Fragment key={attachment.id || index}>
                      <TableRow
                        hover
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getFileIcon(attachment.content_type)}
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {attachment.filename || 'Unknown'}
                              </Typography>
                              {attachment.original_filename &&
                               attachment.original_filename !== attachment.filename && (
                                <Typography variant="caption" color="text.secondary">
                                  Original: {attachment.original_filename}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatFileSize(attachment.size)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                            {attachment.content_type || 'Unknown'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {attachment.uploaded_by || attachment.external_party || 'Unknown'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={uploadedDate ? uploadedDate.toLocaleString() : 'Unknown'}
                            arrow
                          >
                            <Typography variant="body2" color="text.secondary">
                              {uploadedDate
                                ? formatDistanceToNow(uploadedDate, { addSuffix: true })
                                : 'Unknown'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Download" arrow>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleDownload(attachment)}
                              disabled={!attachment.id}
                            >
                              <Download fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      {/* Image preview row */}
                      {isImageType(attachment.content_type) && previewUrl && (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ py: 1 }}>
                            <Box
                              component="img"
                              src={previewUrl}
                              alt={attachment.filename || 'Preview'}
                              sx={{
                                maxWidth: '100%',
                                maxHeight: 300,
                                borderRadius: 1,
                                display: 'block',
                                mx: 'auto',
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default AttachmentsModal
